/**
 * COC-EOD-REPORT-V1 (activity 23) -- the COC role's Daily report
 * (/dashboard/coc/daily-report).
 *
 * The difference from CocDashboardPage (the COC's real-time "My clinics"
 * board) is the JOB: that board answers "what is happening right now"; this
 * answers "what happened on this day." It does not modify or replace that
 * board. A DATE-NAVIGABLE report; "today" is simply the default day, never a
 * today-only screen. Read-only: every control here is a filter, nothing
 * writes.
 *
 * RENDER + ROUTE ONLY -- ZERO NEW BACKEND CODE. This page adds no backend
 * route, no new SQL read, and no new fetch function to dashboard/api.ts. It
 * composes THREE already-open, already-role-fenced reads exactly as their
 * existing pages already call them:
 *   - fetchCocClinics()              -- the SAME call CocDashboardPage makes,
 *     for the clinic list this COC is scoped to (any day: a clinic roster
 *     does not change per day).
 *   - fetchTodayAppointments(clinicId, 'board', businessDate) per clinic --
 *     the SAME clinic day-board read ManagerOpsCockpit already calls for its
 *     day-flow tiles, widened with the board's OWN pre-existing `date`
 *     parameter (any day; COC is already in DAY_BOARD_ROLES). Folded through
 *     deriveDayCommandTiles / sumDayCommandTiles -- ManagerOpsCockpit's own
 *     pure derivations, imported verbatim, never re-derived.
 *   - fetchCocStation() -- the SAME call CocStationSection already makes for
 *     the COC's live work queue, fetched ONLY when the selected day is
 *     today, folded through slaBreakdownRows (this page's own view-model;
 *     see coc-daily-report-view.ts for exactly what it adds over the
 *     station's existing stationSlaBreachCounts tally).
 *
 * PAST-DAY HONESTY (inherited from PR #630 -- see docs/AGENT-RUNS/
 * CALLCENTRE-EOD-V1.md and this page's view-model header for the full
 * reasoning): the day-flow/money tiles read each appointment's STORED status
 * for the requested day (no age-relative math) so they render for ANY day.
 * The SLA-breach breakdown is a LIVE, current-instant classification -- it
 * is fetched and shown ONLY for today; a past day shows an explicit,
 * honest note instead of a stale or fabricated figure.
 *
 * EXCLUDED, NAMED (never silently missing): a count of how long unclaimed
 * leads have been waiting past the follow-up deadline (the "escalation"
 * concept on the manager-tier cockpit) has no equivalent COC-scoped read
 * anywhere in this product today; building one would mean either a new read
 * or widening a manager-only endpoint to admit `coc` -- this package does
 * neither (it widens no role's access to anything), so the figure is named
 * as missing rather than guessed at or silently dropped.
 *
 * PALETTE: docs/design/design-language/02-DESIGN-TOKENS.md section 10b
 * (RATIFIED 2026-08-25). No state chip from the 14-state matrix renders here
 * (day-flow tiles are aggregate counts, not per-record state labels; SLA
 * breach classes have no member in that matrix). The urgency ramp's 'soon'/
 * 'breached' steps render SLA-breach severity, row-level text + icon only,
 * never a pill and never stacked with a second urgency signal per row --
 * identical treatment to the sibling call-centre report, reusing the SAME
 * classifier.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { fetchCocClinics, fetchCocStation, fetchTodayAppointments } from '@/dashboard/api';
import type { CocStationRowView } from '@/dashboard/api';
import type { ClinicRosterDoctor, CocClinicSummary, TodayAppointment } from '@/dashboard/types';
import {
  deriveDayCommandTiles,
  inrLabel,
  sumDayCommandTiles,
  type CancelReasonBreakdownRow,
  type DayCommandTiles,
} from '@/dashboard/manager-tracking/manager-cockpit-view';
import { deskActionCounts, type DeskActionCounts } from '@/dashboard/appointments/ClinicDayBoard';
import {
  classMeta,
  doctorFilterOptions,
  formatLeakageAge,
  matchesDoctorFilter,
  proportionLabel,
  slaBreakdownRows,
  urgencyStepForSeverity,
  type SlaClassBreakdownRow,
  type UrgencyStep,
} from './coc-daily-report-view';
import {
  AcButton,
  clinicDateLabel,
  DataTable,
  DateCommandBar,
  EmptyState,
  ErrorState,
  FilterBar,
  KpiTile,
  LoadingState,
  PageHeader,
  SectionBand,
  useDayScope,
  type DataTableColumn,
} from '@/dashboard/ui';

export const CDR_ESCALATION_EXCLUSION_NOTE =
  'This report does not include a count of long-unclaimed leads waiting past the follow-up deadline -- that count is not on this report yet, and adding it would need either a new read or wider access this report does not have.';

export const CDR_SLA_LIVE_ONLY_NOTE =
  'Open and breached SLA counts are a live, right-now fact -- available only when today is selected. Switch to today to see the current SLA picture.';

// ---- Small inline icons (soon = outline clock, breached = filled alert) ---
// Mirrors the sibling call-centre report's page-local urgency mark
// (frontend/src/dashboard/callcenter/daily-report/CallCenterDailyReportPage.tsx)
// exactly -- same ratified treatment, re-authored per-page since that page's
// own icons are not exported as a shared primitive (no icon system exists
// yet in this codebase for urgency, confirmed absent by the sibling
// package). Never emoji; a single currentColor stroke, no second fill.

function ClockOutlineIcon(): JSX.Element {
  return (
    <svg className="cocdr-urgency-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 4.5V8l2.5 1.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function AlertFilledIcon(): JSX.Element {
  return (
    <svg className="cocdr-urgency-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M8 1.5 15 14H1L8 1.5Z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <rect x="7.25" y="6" width="1.5" height="4" fill="currentColor" />
      <rect x="7.25" y="11" width="1.5" height="1.5" fill="currentColor" />
    </svg>
  );
}

/** Row-level urgency mark: text + icon, never a pill/chip (10b.1). Renders
 *  nothing for 'none' -- absence of decoration IS that step's treatment. */
function UrgencyMark({ step, ageMinutes }: { readonly step: UrgencyStep; readonly ageMinutes?: number }): JSX.Element | null {
  if (step === 'none') return null;
  const label = ageMinutes !== undefined ? formatLeakageAge(ageMinutes) : step === 'soon' ? 'Soon' : 'Breached';
  return (
    <span className={`cocdr-urgency cocdr-urgency--${step}`}>
      {step === 'soon' ? <ClockOutlineIcon /> : <AlertFilledIcon />}
      {label}
    </span>
  );
}

interface PerClinicTiles {
  readonly clinicId: number;
  readonly clinicName: string;
  readonly tiles: DayCommandTiles;
}

/** EXISTING-SCREEN-COMPLETIONS-V1: the RAW per-clinic board rows (plus the
 *  wire's own roster) now live in state, so the doctor filter can re-derive
 *  every tile from the SAME rows without a refetch -- one filter
 *  application point, counts == list under any selection. */
interface PerClinicDay {
  readonly clinicId: number;
  readonly clinicName: string;
  readonly rows: readonly TodayAppointment[];
  readonly doctors: readonly ClinicRosterDoctor[];
}

interface ReportState {
  readonly businessDate: string;
  readonly isToday: boolean;
  readonly totalClinics: number;
  readonly perClinic: readonly PerClinicDay[];
  readonly failedClinics: readonly string[];
  /** null = not today (never fetched) or today but the read failed --
   *  slaUnavailable disambiguates the two on-screen (honest states law). */
  readonly slaRows: readonly CocStationRowView[] | null;
  readonly slaUnavailable: boolean;
}

function CancelReasonList({ reasons }: { readonly reasons: readonly CancelReasonBreakdownRow[] }): JSX.Element {
  return (
    <ul className="cockpit-reasons" data-testid="cocdr-cancel-reasons">
      {reasons.map((r) => (
        <li key={r.code ?? 'no_reason'} className="cockpit-reasons__row">
          <span>{r.label}</span>
          <strong>{r.count}</strong>
        </li>
      ))}
    </ul>
  );
}

function slaColumns(): DataTableColumn<SlaClassBreakdownRow & { readonly isTotal?: boolean; readonly key: string; readonly label: string }>[] {
  return [
    { key: 'label', header: 'Where work is stuck', render: (r) => r.label },
    {
      key: 'urgency',
      header: 'Urgency',
      render: (r) => (r.isTotal ? null : <UrgencyMark step={urgencyStepForSeverity(r.severity)} />),
    },
    { key: 'count', header: 'Count', align: 'end', render: (r) => String(r.count) },
  ];
}

function clinicColumns(): DataTableColumn<PerClinicTiles>[] {
  return [
    { key: 'clinic', header: 'Clinic', render: (r) => r.clinicName },
    { key: 'booked', header: 'Booked', align: 'end', render: (r) => String(r.tiles.bookedToday) },
    { key: 'fulfilled', header: 'Fulfilled', align: 'end', render: (r) => String(r.tiles.fulfilled) },
    { key: 'cancelled', header: 'Cancelled', align: 'end', render: (r) => String(r.tiles.cancelled) },
    {
      key: 'collect',
      header: 'To collect',
      align: 'end',
      render: (r) =>
        r.tiles.collect.totalAmount !== null ? inrLabel(r.tiles.collect.totalAmount) : String(r.tiles.collect.count),
    },
    { key: 'paid', header: 'Paid', align: 'end', render: (r) => String(r.tiles.paidTodayCount) },
  ];
}

export default function CocDailyReportPage(): JSX.Element {
  // THE DAY AXIS: the registered day-scope primitive (DAY-SCOPE-PRIMITIVE-
  // AND-ADOPTION-V1), the SAME one the Appointments planning calendar and
  // the Doctor's Day cockpit already consume -- binds the selected day to
  // the URL, owns the today/past/future verdict, and hands the DateCommandBar
  // below its three moves. This page mints no second day model.
  const dayScope = useDayScope();
  const [report, setReport] = useState<ReportState | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  // EXISTING-SCREEN-COMPLETIONS-V1: the doctor selection (multi-select
  // doctor-id tokens + the Unassigned token). Empty = no filter -- the
  // page's default render stays byte-identical to the pre-filter page.
  const [selectedDoctors, setSelectedDoctors] = useState<readonly string[]>([]);

  const load = useCallback(async () => {
    setState((prev) => (prev === 'ready' ? 'ready' : 'loading'));
    try {
      const businessDate = dayScope.date;
      const isToday = dayScope.scope === 'today';

      const clinics: CocClinicSummary[] = await fetchCocClinics();
      const boardResults = await Promise.allSettled(
        clinics.map((c) =>
          fetchTodayAppointments(Number(c.clinicId), 'board', businessDate).then((d) => ({
            clinicId: Number(c.clinicId),
            clinicName: c.clinicName,
            rows: d.appointments,
            doctors: d.clinicDoctors ?? [],
          })),
        ),
      );
      const perClinic: PerClinicDay[] = [];
      const failedClinics: string[] = [];
      boardResults.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          perClinic.push(r.value);
        } else {
          failedClinics.push(clinics[i]?.clinicName ?? `Clinic ${clinics[i]?.clinicId ?? ''}`);
        }
      });

      let slaRows: readonly CocStationRowView[] | null = null;
      let slaUnavailable = false;
      if (isToday) {
        try {
          const station = await fetchCocStation();
          slaRows = station.rows;
        } catch {
          slaUnavailable = true;
        }
      }

      setReport({
        businessDate,
        isToday,
        totalClinics: clinics.length,
        perClinic,
        failedClinics,
        slaRows,
        slaUnavailable,
      });
      setErrorDetail(null);
      setState('ready');
    } catch (err) {
      setErrorDetail(err instanceof Error ? err.message : 'Load failed');
      setState((prev) => (prev === 'ready' ? 'ready' : 'error'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayScope.date]);

  useEffect(() => {
    void load();
  }, [load]);

  // EXISTING-SCREEN-COMPLETIONS-V1: ONE derivation pass over the fetched
  // rows -- the doctor filter is applied ONCE here, and every row-derived
  // figure below (flow tiles, cancel reasons, money row, per-clinic table,
  // and the today-only Expected / Waiting / Needs-registration strip) reads
  // this pass, so the page can never disagree with itself under a
  // selection. The filter options derive from the UNFILTERED wire facts
  // (roster + rows). The SLA section is NOT row-derived (it is the COC
  // work-queue read, which carries no doctor attribution) -- the filter
  // structurally cannot apply to it.
  const derived = useMemo(() => {
    if (report === null) return null;
    const filteredPerClinic: PerClinicTiles[] = report.perClinic.map((c) => ({
      clinicId: c.clinicId,
      clinicName: c.clinicName,
      tiles: deriveDayCommandTiles(c.rows.filter((r) => matchesDoctorFilter(r, selectedDoctors))),
    }));
    const tiles =
      filteredPerClinic.length === 0
        ? null
        : sumDayCommandTiles(filteredPerClinic.map((c) => c.tiles));
    // A6 TRUTH LAW (the front-desk strip's own posture): Waiting and
    // Needs-registration are RIGHT-NOW facts -- derived and rendered for
    // today only, through THE ONE deskActionCounts derivation the front
    // desk's tiles already consume. Off today they are structurally
    // withheld, never relabelled.
    const desk: DeskActionCounts | null = report.isToday
      ? deskActionCounts(
          report.perClinic.flatMap((c) =>
            c.rows.filter((r) => matchesDoctorFilter(r, selectedDoctors)),
          ),
          Date.now(),
        )
      : null;
    return {
      perClinic: filteredPerClinic,
      tiles,
      desk,
      doctorOptions: doctorFilterOptions(report.perClinic),
    };
  }, [report, selectedDoctors]);

  if (state === 'error') {
    return (
      <div className="ac-band-flow" data-testid="cocdr-page">
        <PageHeader title="Daily report" subs={['COC - All times IST']} />
        <ErrorState
          title="This report failed to load"
          detail={errorDetail ?? 'The report did not answer.'}
          action={
            <AcButton size="sm" onClick={() => void load()}>
              Retry
            </AcButton>
          }
        />
      </div>
    );
  }

  if (state === 'loading' || report === null) {
    return (
      <div className="ac-band-flow" data-testid="cocdr-page">
        <PageHeader title="Daily report" subs={['COC - All times IST']} />
        <LoadingState label="Loading the daily report" />
      </div>
    );
  }

  const slaBreakdown = report.slaRows !== null ? slaBreakdownRows(report.slaRows) : [];
  const slaTotal = slaBreakdown.reduce((s, r) => s + r.count, 0);
  const slaTableRows =
    slaBreakdown.length === 0
      ? []
      : [
          ...slaBreakdown.map((r) => ({ ...r, key: r.breachClass, label: classMeta(r.breachClass).title, isTotal: false })),
          { key: '__total', breachClass: slaBreakdown[0]!.breachClass, severity: 'neutral' as const, label: 'Total', count: slaTotal, isTotal: true },
        ];

  return (
    <div className="ac-band-flow" data-testid="cocdr-page">
      <PageHeader title="Daily report" subs={['COC - All times IST']} />
      <p className="cocdr-exclusion-note" data-testid="cocdr-escalation-note">
        {CDR_ESCALATION_EXCLUSION_NOTE}
      </p>

      <DateCommandBar
        date={dayScope.date}
        today={dayScope.today}
        onSelect={dayScope.selectDate}
        onStep={dayScope.stepDay}
        onToday={dayScope.goToday}
        label="Clinic day"
        testId="cocdr-daybar"
      />

      {/* EXISTING-SCREEN-COMPLETIONS-V1 (tenant tracker, COC home dashboard
          "Individual or multi Doctor selection option"): THE registered
          FilterBar's multi-select tokens group over the board wire's own
          doctor facts. Every row-derived figure below reads the filtered
          rows (one application point); an empty selection is the unfiltered
          page, byte-identical. Rendered only when the day carries a doctor
          to pick (a control with nothing to steer is a dead door, LES-016). */}
      {derived !== null && derived.doctorOptions.length > 0 ? (
        <FilterBar
          groups={[
            {
              kind: 'tokens',
              key: 'doctor',
              label: 'Doctor',
              options: derived.doctorOptions,
              selected: selectedDoctors,
            },
          ]}
          onToggleToken={(_key, token, nowSelected) =>
            setSelectedDoctors((prev) =>
              nowSelected ? [...prev, token] : prev.filter((t) => t !== token),
            )
          }
          onChoice={() => undefined}
          onFlag={() => undefined}
          onReset={() => setSelectedDoctors([])}
        />
      ) : null}

      {/* ---- Day flow: what happened to today's appointments (any day) ---- */}
      <div className="cockpit-tile-row" data-testid="cocdr-flow-row">
        {derived !== null && derived.tiles !== null ? (
          <>
            <KpiTile label="Booked" value={derived.tiles.bookedToday} sub="Full day's slate" testId="cocdr-tile-booked" />
            <KpiTile label="Arrived" value={derived.tiles.arrived} testId="cocdr-tile-arrived" />
            <KpiTile label="Checked in" value={derived.tiles.checkedIn} testId="cocdr-tile-checkedin" />
            <KpiTile
              label="In consult"
              value={derived.tiles.inConsult}
              tone={derived.tiles.inConsult > 0 ? 'warm' : 'ok'}
              testId="cocdr-tile-inconsult"
            />
            <KpiTile label="Fulfilled" value={derived.tiles.fulfilled} testId="cocdr-tile-fulfilled" />
            <KpiTile
              label="Cancelled"
              value={derived.tiles.cancelled}
              sub={derived.tiles.cancelled > 0 ? 'Reasons below' : undefined}
              tone={derived.tiles.cancelled > 0 ? 'breach' : 'ok'}
              testId="cocdr-tile-cancelled"
            />
          </>
        ) : (
          <p className="cockpit-unavailable" data-testid="cocdr-flow-unavailable">
            {report.totalClinics === 0
              ? 'No clinics assigned to your scope.'
              : 'Day board unavailable for this day - counts cannot be shown right now.'}
          </p>
        )}
      </div>
      {derived !== null && derived.tiles !== null && derived.tiles.cancelReasons.length > 0 ? (
        <CancelReasonList reasons={derived.tiles.cancelReasons} />
      ) : null}

      {/* EXISTING-SCREEN-COMPLETIONS-V1 (tenant tracker, COC home dashboard
          "Patients Expected" / "Waiting" / "Needs registration"): the
          RIGHT-NOW strip, TODAY ONLY (the front-desk strip's A6 posture --
          off today these would advertise arrivals that never happened, so
          they are withheld, never relabelled). Expected counts the
          pre-arrival stored statuses (DAY_EXPECTED_STATUSES, the doctor
          Expected band's vocabulary); Waiting and Needs registration are
          THE ONE deskActionCounts derivation the front desk's own tiles
          consume -- the same numbers the desk sees, on the COC's scope. */}
      {derived !== null && derived.tiles !== null && derived.desk !== null ? (
        <div className="cockpit-tile-row" data-testid="cocdr-now-row">
          <KpiTile
            label="Patients expected"
            value={derived.tiles.expected}
            sub={derived.tiles.expected === 0 ? 'Everyone booked has arrived or closed' : 'Not yet arrived'}
            testId="cocdr-tile-expected"
          />
          <KpiTile
            label="Waiting"
            value={derived.desk.waiting}
            sub={derived.desk.waiting === 0 ? 'No one in the clinic yet' : 'In clinic, before the doctor'}
            testId="cocdr-tile-waiting"
          />
          <KpiTile
            label="Needs registration"
            value={derived.desk.needsRegistration}
            tone={derived.desk.needsRegistration > 0 ? 'warm' : 'ok'}
            sub={
              derived.desk.needsRegistration === 0
                ? 'Every arrival has a record'
                : 'Finish registration at the desk'
            }
            testId="cocdr-tile-needsreg"
          />
        </div>
      ) : null}

      {/* ---- Money: recorded evidence for this day (any day) ---- */}
      {derived !== null && derived.tiles !== null ? (
        <div className="cockpit-tile-row" data-testid="cocdr-money-row">
          <KpiTile
            label="To collect"
            value={
              derived.tiles.collect.totalAmount !== null
                ? inrLabel(derived.tiles.collect.totalAmount)
                : `${derived.tiles.collect.count} visit${derived.tiles.collect.count === 1 ? '' : 's'}`
            }
            sub={
              derived.tiles.collect.count > 0
                ? `${derived.tiles.collect.count} visit${derived.tiles.collect.count === 1 ? '' : 's'} awaiting collection`
                : 'Nothing awaiting collection'
            }
            tone={derived.tiles.collect.count > 0 ? 'warm' : 'ok'}
            testId="cocdr-tile-collect"
          />
          <KpiTile
            label="Paid"
            value={derived.tiles.paidTodayCount}
            sub="Visits with recorded payment evidence"
            tone="ok"
            testId="cocdr-tile-paid"
          />
        </div>
      ) : null}

      {report.failedClinics.length > 0 ? (
        <p className="cockpit-unavailable" data-testid="cocdr-failed-clinics">
          Not included (day board unavailable): {report.failedClinics.join(', ')}
        </p>
      ) : null}

      {/* ---- Per-clinic breakdown (COC's own multi-clinic scope) ---- */}
      <SectionBand
        title="By clinic"
        basis={proportionLabel(report.perClinic.length, report.totalClinics)}
        testId="cocdr-clinic-section"
      >
        <DataTable
          columns={clinicColumns()}
          rows={derived?.perClinic ?? []}
          rowKey={(r) => r.clinicId}
          label="Clinics"
          testId="cocdr-clinic-table"
          empty={<EmptyState title="No clinics to show" body="No clinics assigned to your scope reported for this day." />}
        />
      </SectionBand>

      {/* ---- SLA breach breakdown (TODAY only -- live, right-now fact) ---- */}
      <SectionBand
        title="Breached SLA"
        basis={report.isToday ? `${slaTotal} open, right now` : clinicDateLabel(report.businessDate)}
        testId="cocdr-sla-section"
      >
        {report.isToday ? (
          report.slaUnavailable ? (
            <p className="cockpit-unavailable" data-testid="cocdr-sla-unavailable">
              SLA read unavailable right now.
            </p>
          ) : (
            <DataTable
              columns={slaColumns()}
              rows={slaTableRows}
              rowKey={(r) => r.key}
              rowClassName={(r) => (r.isTotal ? 'cocdr-row--total' : r.severity === 'red' ? 'cocdr-row--breached' : undefined)}
              label="SLA breaches by class"
              testId="cocdr-sla-table"
              empty={<EmptyState title="No open SLA breaches." body="Nothing has crossed its SLA threshold right now." />}
            />
          )
        ) : (
          <p className="cocdr-exclusion-note" data-testid="cocdr-sla-live-only-note">
            {CDR_SLA_LIVE_ONLY_NOTE}
          </p>
        )}
      </SectionBand>
    </div>
  );
}
